/**
 * FacetWP's pager only renders a `.next` link when a next page exists, and
 * never renders a `.prev` link at all. This always shows both arrows,
 * disabling whichever one doesn't apply on the current page — matching
 * the always-visible prev/next behaviour of the custom listing pager.
 *
 * Runs on every `facetwp-loaded` since FacetWP rebuilds the pager markup
 * on every refresh (initial load, filtering, page change).
 *
 * Native DOM, no jQuery. This module runs immediately on import (from
 * app.js), and jQuery isn't guaranteed to exist on the page yet — or at
 * all — at that point (only FacetWP's own scripts depend on it, and this
 * runs on every page where the archive template loads, FacetWP active or
 * not). A jQuery IIFE would throw a ReferenceError on `jQuery` being
 * undefined and take the whole bundle down with it, including
 * facetwp-dropdowns.js imported right after this file. `facetwp-loaded` is
 * a plain DOM CustomEvent, so jQuery was never actually required here.
 *
 * Arrows are deliberately empty <a> elements — the arrow glyph itself is a
 * CSS ::before background-image (main.scss:
 * `.facetwp-page.prev:before`/`.facetwp-page.next:before`), not text;
 * printing a text glyph here would double up with it. Disabled state uses
 * the design's own class, `isDisabled` (main.scss:
 * `.facetwp-page.prev.isDisabled`/`.facetwp-page.next.isDisabled`) — not
 * the generic `disabled`, which main.scss doesn't style at all.
 *
 * No aria-label on the arrows: there's no front-end i18n bridge available
 * in this plain JS helper (window.nmhg's i18n strings are wired for the
 * block editor only, see app/Setup/editor.php) and wiring one just for
 * this would be out of scope here — left unlabeled rather than hardcoding
 * an English string.
 */
(function () {
  'use strict';

  function buildArrow(direction, page, isDisabled) {
    var a = document.createElement('a');

    a.className = 'facetwp-page ' + direction + (isDisabled ? ' isDisabled' : '');
    a.setAttribute('href', '#');
    a.setAttribute('data-page', page);

    if (isDisabled) {
      a.setAttribute('aria-disabled', 'true');
    }

    return a;
  }

  function ensureArrows() {
    document
      .querySelectorAll('.facetwp-facet-pagination .facetwp-pager')
      .forEach(function (pager) {
        // Avoid duplicating arrows if this pager was already processed
        // for the current render.
        pager
          .querySelectorAll('.facetwp-page.prev, .facetwp-page.next')
          .forEach(function (el) {
            el.remove();
          });

        var pages = Array.prototype.filter.call(
          pager.querySelectorAll('.facetwp-page'),
          function (el) {
            return !el.classList.contains('dots');
          },
        );

        if (!pages.length) {
          return;
        }

        var active = pages.filter(function (el) {
          return el.classList.contains('active');
        })[0];

        var currentPage = parseInt(active && active.dataset.page, 10) || 1;

        var first = pages[0];
        var last = pages[pages.length - 1];

        var firstPage = parseInt(first.dataset.page, 10) || 1;
        var lastPage = parseInt(last.dataset.page, 10) || firstPage;

        var isFirst = currentPage <= firstPage;
        var isLast = currentPage >= lastPage;

        var prev = buildArrow('prev', Math.max(currentPage - 1, firstPage), isFirst);
        var next = buildArrow('next', Math.min(currentPage + 1, lastPage), isLast);

        pager.insertBefore(prev, pager.firstChild);
        pager.appendChild(next);
      });
  }

  document.addEventListener('facetwp-loaded', ensureArrows);

  document.addEventListener('click', function (e) {
    var arrow = e.target.closest(
      '.facetwp-facet-pagination .facetwp-page.prev, .facetwp-facet-pagination .facetwp-page.next',
    );

    if (!arrow) {
      return;
    }

    e.preventDefault();

    if (arrow.classList.contains('isDisabled') || arrow.getAttribute('aria-disabled') === 'true') {
      return false;
    }
  });
})();

/**
 * Скрол до верху листингу після переходу по сторінках FacetWP.
 *
 * FacetWP підмінює вміст .facetwp-template на місці, не перезавантажуючи
 * сторінку. Клікнувши «2» внизу довгого листингу, користувач лишається
 * унизу — перед ним кінець уже нового списку, а не його початок. Тому після
 * переходу сторінку треба підняти до верху фідів.
 *
 * ЛИШЕ пагінація. Подія 'facetwp-loaded' спільна для всіх рефрешів —
 * першого рендера, фільтрації, скидання фільтрів, — а скрол потрібен рівно
 * на зміні сторінки. Розрізнити їх самою подією не можна: FWP.paged на
 * фільтрації теж змінюється (скидається в 1), тож порівняння номера сторінки
 * давало б хибне спрацювання. Звідси прапорець: клік по пагінатору його
 * зводить, найближчий 'facetwp-loaded' його зчитує й одразу гасить.
 *
 * Клік слухаємо на document у фазі СПЛИВАННЯ, як і обробник стрілок вище.
 * Порядок із власним обробником FacetWP (jQuery, теж делегований на document)
 * на сам рефреш не впливає — ми лише ставимо прапорець, — але на стан вузла
 * впливає: FacetWP реєструється раніше й ставить клікнутому пункту '.active'
 * СИНХРОННО, ще до нашого обробника. Тому перевіряти тут '.active' не можна:
 * до нас будь-який клік по номеру приходить уже позначеним, і умова відкидала
 * б геть усі переходи. Клік по вже активній сторінці нічого не ламає — FacetWP
 * робить звичайний рефреш, прапорець на ньому ж і згорає.
 *
 * Пропускаємо '.dots' і вимкнені стрілки: по них рефрешу немає, прапорець
 * лишився б зведеним і «вистрілив» би на наступній фільтрації. Підстраховує
 * таймаут: якщо рефреш чомусь не стався (мережа, скасований запит), прапорець
 * гасне сам і не тягнеться в наступну дію.
 *
 * Ціль шукаємо від самого пагінатора, а не через document: у шаблонах
 * '.pagination' — прямий сусід '.facetwp-template' одразу після нього
 * (див. коментар у partials/archive-feed.blade.php), тож достатньо пройти
 * попередніми сусідами. document.querySelector лишається запасним варіантом
 * на випадок іншої обгортки.
 *
 * Офсет — висота '.header' плюс 20px повітря. Хедер липкий і накриває
 * контент, тому без цієї поправки верх списку опинився б під ним; ту саму
 * '.header'.offsetHeight як офсет бере й обробник якорів у main.js (§7.5).
 */
(function () {
  'use strict';

  var GAP = 20;
  var TIMEOUT = 5000;

  var pendingTarget = null;
  var pendingTimer = null;

  function clearPending() {
    pendingTarget = null;

    if (pendingTimer) {
      clearTimeout(pendingTimer);
      pendingTimer = null;
    }
  }

  function findTemplate(link) {
    var node = link.closest('.pagination') || link.closest('.facetwp-facet-pagination') || link;
    var prev = node.previousElementSibling;

    while (prev) {
      if (prev.classList.contains('facetwp-template')) {
        return prev;
      }

      var inner = prev.querySelector('.facetwp-template');

      if (inner) {
        return inner;
      }

      prev = prev.previousElementSibling;
    }

    return document.querySelector('.facetwp-template');
  }

  function scrollToTemplate(el) {
    var header = document.querySelector('.header');
    var offset = header ? header.offsetHeight : 0;
    var top = window.scrollY + el.getBoundingClientRect().top - offset - GAP;

    var reduced =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({
      top: Math.max(top, 0),
      behavior: reduced ? 'auto' : 'smooth',
    });
  }

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) {
      return;
    }

    var link = e.target.closest('.facetwp-facet-pagination .facetwp-page');

    if (!link) {
      return;
    }

    if (
      link.classList.contains('dots') ||
      link.classList.contains('isDisabled') ||
      link.getAttribute('aria-disabled') === 'true'
    ) {
      return;
    }

    var target = findTemplate(link);

    if (!target) {
      return;
    }

    clearPending();

    pendingTarget = target;
    pendingTimer = setTimeout(clearPending, TIMEOUT);
  });

  document.addEventListener('facetwp-loaded', function () {
    if (!pendingTarget) {
      return;
    }

    var target = pendingTarget;

    clearPending();

    // Міряємо одразу, без requestAnimationFrame: нас цікавить ВЕРХ
    // контейнера, а на нього вміст списку не впливає — усе, що вище (герой,
    // фільтри), рефреш не чіпає. Тож чекати дорендерювання карток нема сенсу,
    // зате rAF у фоновій вкладці приспаний, і скрол відклався б на невизначено.
    scrollToTemplate(target);
  });
})();
